import { useEffect, useState } from 'react';
import { activityLogsApi } from '../api/endpoints';
import { MagnifyingGlassIcon, XMarkIcon, ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../utils/helpers';
import { Button } from '../components/ui/Button';
import { DatePicker } from '../components/ui/DatePicker';
import Listbox, { type ListboxOptionType } from '../components/ui/Listbox';
import { useCallback } from 'react';
import LogDetailPanel from '../components/LogDetailPanel';
import SpotlightCard from '../components/ui/SpotlightCard';
import { InteractiveList } from '../components/ui/PhysicsList';
import PageHeader from '../components/ui/PageHeader';
import { useLogsStore } from '../stores/logsStore';

interface Log {
  id: string;
  admin_id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details: string;
  created_at: number;
}

interface LogFilters {
  action?: string;
  startDate?: number;
  endDate?: number;
}

const ACTION_OPTIONS: ListboxOptionType[] = [
  { value: '', label: '全部操作' },
  { value: 'LOGIN', label: '登录' },
  { value: 'LOGOUT', label: '登出' },
  { value: 'RETRY_TASK', label: '重试任务' },
  { value: 'CANCEL_TASK', label: '取消任务' },
  { value: 'BATCH_CANCEL_TASKS', label: '批量取消任务' },
  { value: 'BATCH_DELETE_TASKS', label: '批量删除任务', variant: 'danger' },
  { value: 'VIEW_TASK', label: '查看任务' },
  { value: 'DELETE_TASK', label: '删除任务', variant: 'danger' },
];

export default function Logs() {
  const { logs, loading, pagination, filters, isCacheValid, setLogs, setLoading, setPagination, setFilters, invalidateCache } = useLogsStore();
  const [error, setError] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await activityLogsApi.getActivityLogs({
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...filters,
      });

      if (response.success && response.data) {
        if ('data' in response.data && Array.isArray(response.data.data)) {
          setLogs(response.data.data);
          const paginationData = response.data?.pagination;
          if (paginationData) {
            setPagination(paginationData);
          }
        } else if (Array.isArray(response.data)) {
          setLogs(response.data);
        } else {
          setLogs([]);
        }
      }
    } catch (err) {
      setError('加载日志失败');
      console.error('Logs load error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize, setLogs, setLoading, setPagination]);

  useEffect(() => {
    const cacheIsValid = isCacheValid();
    const hasCachedData = logs.length > 0;

    if (cacheIsValid && hasCachedData) {
      return;
    }

    loadLogs();
  }, [loadLogs, isCacheValid, logs.length]);

  const handleFilterChange = (key: keyof LogFilters, value: string | number | undefined) => {
    setFilters({ [key]: value });
    setPagination({ page: 1 });
  };

  const clearFilters = () => {
    setFilters({});
    setPagination({ page: 1 });
  };

  const getActionText = (action: string) => {
    const actionMap: Record<string, string> = {
      LOGIN: '登录',
      LOGOUT: '登出',
      RETRY_TASK: '重试任务',
      CANCEL_TASK: '取消任务',
      BATCH_CANCEL_TASKS: '批量取消任务',
      BATCH_DELETE_TASKS: '批量删除任务',
      VIEW_TASK: '查看任务',
      DELETE_TASK: '删除任务',
    };
    return actionMap[action] || action;
  };

  const getResourceTypeText = (type: string) => {
    const typeMap: Record<string, string> = {
      TASK: '任务',
      INSTANCE: '实例',
      ADMIN: '管理员',
    };
    return typeMap[type] || type;
  };

  const hasActiveFilters = filters.action || filters.startDate || filters.endDate;

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const params: Record<string, string | number | undefined> = {};
      if (filters.action) params.action = filters.action;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const blob = await activityLogsApi.exportCSV(params);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity_logs_export_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        alert('导出失败');
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('导出失败');
    } finally {
      setExportLoading(false);
    }
  };

  const handleViewDetails = (log: Log) => {
    if (selectedLog?.id === log.id) {
      setIsPanelOpen(false);
      setSelectedLog(null);
    } else {
      setSelectedLog(log);
      setIsPanelOpen(true);
    }
  };

  return (
    <div className="flex w-full overflow-hidden">
      <div className="flex-1 min-w-0 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]">
        <PageHeader
          title="操作日志"
          actions={
            <div className="flex gap-3">
              <Button
                variant="default"
                onClick={handleExportCSV}
                disabled={exportLoading}
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {exportLoading ? '导出中...' : '导出 CSV'}
              </Button>
              <Button variant="default" onClick={() => { invalidateCache(); loadLogs(); }}>
                <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </Button>
            </div>
          }
        />

        {error && (
          <div className="p-4 mb-6 text-sm text-red-400 bg-red-500/10 ring-1 ring-red-500/20 rounded-xl">
            {error}
          </div>
        )}

        <SpotlightCard className="p-6 mb-8 bg-zinc-900/40 backdrop-blur-xl ring-1 ring-white/10" enablePhysics={true}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Listbox
              label="操作类型"
              options={ACTION_OPTIONS}
              value={filters.action || ''}
              onChange={(value) => handleFilterChange('action', value || undefined)}
            />

            <DatePicker
              label="开始时间"
              value={filters.startDate}
              onChange={(val) => handleFilterChange('startDate', val)}
              showTime
            />

            <DatePicker
              label="结束时间"
              value={filters.endDate}
              onChange={(val) => handleFilterChange('endDate', val)}
              showTime
            />
          </div>

          {hasActiveFilters && (
            <div className="mt-6 pt-6 -mx-6 px-6 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
              >
                <XMarkIcon className="w-4 h-4 mr-1" />
                清除筛选
              </Button>
            </div>
          )}
        </SpotlightCard>

          <div className="flex flex-col mb-8">
            <div className="flex-1 px-4">
              {loading && logs.length === 0 ? (
                <div className="space-y-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="bg-white/5 rounded-xl p-4 animate-pulse">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-x-3">
                          <div className="h-5 w-16 bg-white/10 rounded animate-pulse" />
                          <div className="h-4 w-24 bg-white/10 rounded animate-pulse" />
                        </div>
                        <div className="h-3 w-32 bg-white/10 rounded animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-20">
                  <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-4 text-white/20" />
                  <p className="text-white/40 font-medium">暂无符合条件的日志</p>
                </div>
              ) : (
                   <InteractiveList
                     items={logs}
                     loading={loading}
                     emptyMessage="暂无符合条件的日志"
                     selectedItemId={selectedLog?.id}
                     onItemClick={handleViewDetails}
                     renderItem={(log: Log) => (
                       <div className="flex items-center justify-between">
                         <div className="min-w-0 flex-1">
                           <div className="flex items-center gap-x-3 mb-2">
                              <span className="inline-flex items-center rounded-md bg-blue-500/[8%] px-2 py-0.5 text-xs font-medium text-blue-400">
                               {getActionText(log.action)}
                             </span>
                            
                            {log.resource_type && (
                              <>
                                <span className="text-zinc-600">/</span>
                                <span className="text-base font-medium text-zinc-100">
                                  {getResourceTypeText(log.resource_type)}
                                </span>
                              </>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-x-2 text-xs text-zinc-600">
                            <time dateTime={new Date(log.created_at).toISOString()}>
                              {formatDate(log.created_at)}
                            </time>
                            <span className="text-zinc-700">·</span>
                            <span className="text-zinc-500 group-hover:text-zinc-400 transition-colors">
                              {log.admin_id}
                            </span>
                          </div>
                        </div>

                        <svg className="h-5 w-5 text-zinc-700 opacity-50 group-hover:opacity-100 transition-all" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path fillRule="evenodd" d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  />
               )}

               {pagination.totalPages > 1 && (
                 <div className="mt-4 px-2 py-4 flex items-center justify-between">
                   <p className="text-sm text-white/30">
                     共 <span className="text-white/60">{pagination.total}</span> 条记录
                   </p>
                   <div className="flex items-center gap-2">
                      <Button
                        size="icon"
                        onClick={() => setPagination({ page: Math.max(1, pagination.page - 1) })}
                        disabled={pagination.page === 1}
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </Button>
                      <div className="flex items-center gap-1 mx-2">
                        <span className="text-sm font-bold text-white">{pagination.page}</span>
                        <span className="text-sm text-white/20">/</span>
                        <span className="text-sm text-white/40">{pagination.totalPages}</span>
                      </div>
                      <Button
                        size="icon"
                        onClick={() => setPagination({ page: Math.min(pagination.totalPages, pagination.page + 1) })}
                        disabled={pagination.page === pagination.totalPages}
                      >
                       <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                       </svg>
                     </Button>
                   </div>
                 </div>
               )}
            </div>
          </div>
      </div>

      <LogDetailPanel 
        isOpen={isPanelOpen} 
        onClose={() => setIsPanelOpen(false)} 
        log={selectedLog} 
      />
    </div>
  );
}